import { ReportConfigV2 } from "@/common/report-configuration";
import Feedback from "@/components/chat/Feedback";
import { ResponseDisplay } from "@/components/ResponseDisplay";
import { Skeleton } from "@/components/ui/skeleton";
import EventBus from "@/lib/eventBus";
import { createReportV2 } from "@/lib/serverComm";
import { Message } from "@/types/chat.ts";
import { DEEP_INSIGHT_CHAT_CONTEXT_SUFFIX } from "@/types/deep-insights.ts";
import React, { useCallback, useEffect, useReducer } from "react";
import { useNavigate } from "react-router-dom";
import { ReportV2 } from "../report/ReportV2";
import {
  reducer as reportDesignerStateReducer,
  ReportDesignerState,
  ReportStateReducerActionType,
} from "@/common/reportDesignerStateReducer";
import { useCurrentWorkspace } from "@/store/authContext/authContext";

interface ChatMessageProps {
  message: Message;
  isLast: boolean;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const isPending = message.status === "pending";
  const navigate = useNavigate();
  const workspace = useCurrentWorkspace();
  const reducerWithOverrides = useCallback(
    (state: ReportDesignerState, action: ReportStateReducerActionType) =>
      reportDesignerStateReducer(
        state,
        action,
        workspace.dataset_field_configs,
      ),
    [workspace.dataset_field_configs],
  );

  // Create a minimal initial state for the reducer
  const initialState: ReportDesignerState = {
    errorMessage: null,
    hasChanges: false,
    isInitializing: false,
    isError: false,
    newFilterDefinitionName: null,
    reportId: null,
    reportIsInSnapshotMode: false,
    reportConfig: message.report_config!,
    toastTitle: null,
    toastDescription: null,
    toastErrorIndex: 0,
  };

  const [state, dispatch] = useReducer(reducerWithOverrides, initialState);

  const split = message.text.split(DEEP_INSIGHT_CHAT_CONTEXT_SUFFIX, 2);
  const text = split.length > 1 ? split[1] : split[0];

  useEffect(() => {
    if (message.report_config) {
      dispatch({ type: "setReportConfig", payload: message.report_config });
    }
  }, [message.report_config]);

  const handleSaveReport = async (config: ReportConfigV2) => {
    const reportConfig = {
      ...config,
      title: config.title || "Untitled Report",
    };
    const report = await createReportV2(reportConfig);
    navigate(`/areport/${report.id}`);
    EventBus.$emit("sidebar:refreshReports");
  };

  return (
    <div key={message.id}>
      <div
        className={
          message.sender === "user"
            ? "flex items-center justify-between gap-3"
            : "flex items-start justify-between gap-3"
        }
      >
        <div
          className={`p-6 ${
            message.sender === "user"
              ? "py-4 text-lg rounded-full bg-white border border-[var(--gradient-color-main-end)] dark:bg-gray-900 max-w-[70%]"
              : "rounded-md border bg-white dark:bg-gray-800 w-full border-gray-200 dark:border-gray-700"
          }`}
        >
          {isPending ? (
            <div className="p-6">
              <Skeleton className="w-full h-4 mb-2" />
              <Skeleton className="w-3/4 h-4 mb-2" />
              <Skeleton className="w-1/2 h-4" />
            </div>
          ) : (
            <>
              <div className="flex flex-col space-y-6">
                <div className="w-full">
                  <ResponseDisplay
                    content={text}
                    isUserMessage={message.sender === "user"}
                  />
                </div>
                {state.reportConfig && (
                  <div className="mt-4">
                    <ReportV2
                      reportConfig={state.reportConfig}
                      reportDesignerDispatch={dispatch}
                      newFilterField={null}
                      allowSave={false}
                    />
                  </div>
                )}
              </div>
              {message.sender === "agent" && message.status !== "pending" && (
                <div className="mt-4 flex justify-end items-center">
                  <div className="flex-1">
                    <button
                      onClick={() =>
                        state.reportConfig &&
                        handleSaveReport(state.reportConfig)
                      }
                      className={`${
                        state.reportConfig ? "" : "hidden"
                      } rounded-full bg-sky-500 flex items-center px-5 py-2 text-sm text-white transition-opacity duration-200 hover:bg-sky-600`}
                    >
                      Save & Modify Report
                    </button>
                  </div>

                  <div
                    className={`inline-flex rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900`}
                  >
                    <Feedback
                      messageId={message.id}
                      currentFeedback={message.feedback}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {message.sender === "user" && message.sent_at && (
          <div className="text-xs text-gray-500 whitespace-nowrap flex-shrink-0">
            {new Date(message.sent_at)
              .toLocaleString("en-US", {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
                month: "short",
                day: "numeric",
                year: "numeric",
              })
              .toUpperCase()}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatMessage;
